import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  AlertTitle,
  CircularProgress,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Pagination,
  LinearProgress,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Recommend as RecsIcon,
  ArrowBack as BackIcon,
  PlayArrow as ActionIcon,
  Close as IgnoreIcon,
  OpenInNew as LinkIcon,
  CheckCircle as AppliedIcon,
  InfoOutlined as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Psychology as ReasoningIcon,
  Code as PromptIcon,
  DataObject as ResponseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listPageAuditRequests,
  getAuditRecommendations,
  applyRecommendation,
  rejectRecommendation,
  type PageAuditRequest,
  type AuditRecommendation,
} from '@/services/customer/auditApi';

const fmt = (d?: string | null) =>
  d ? new Date(d.endsWith('Z') ? d : d + 'Z').toLocaleString() : '—';

const PAGE_SIZE = 20;

const PRIORITY_COLOR: Record<string, 'error' | 'warning' | 'default'> = {
  high: 'error',
  medium: 'warning',
  low: 'default',
};

// ─── Render recommendationData: the actual "what to do" content ──────────────

function BeforeAfter({ label, before, after }: { label: string; before?: string; after?: string }) {
  if (!before && !after) return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ mb: 0.75, letterSpacing: '0.05em' }}>
        {label.toUpperCase()}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {before && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Chip label="Current" size="small" sx={{ minWidth: 72, bgcolor: 'grey.100', flexShrink: 0, mt: 0.25 }} />
            <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.8rem', lineHeight: 1.65, wordBreak: 'break-word' }}>
              {before}
            </Typography>
          </Box>
        )}
        {after && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Chip label="Suggested" size="small" color="primary" sx={{ minWidth: 72, flexShrink: 0, mt: 0.25 }} />
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', lineHeight: 1.65, wordBreak: 'break-word', fontWeight: 600 }}>
              {after}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// Keys handled explicitly — never rendered as raw key-value pairs
const KNOWN_KEYS = new Set([
  'action','recommendation','summary',
  'current_value','suggested_value',
  'current_title','suggested_title',
  'current_description','suggested_description',
  'steps',
  // LLM internals — shown only in detail dialog
  'reasoning','llm_prompt','llm_response','llm_model',
  // items[] wrapper key
  'items',
]);

interface RecommendationItem {
  title: string;
  issue: string;
  category?: string;
  priority?: string;
  effort?: string;
  impact?: string;
  reasoning?: string;
  suggested_value?: string;
  current_value?: string | null;
  target_query?: string | null;
  llm_model?: string;
  llm_prompt?: string;
  llm_response?: string;
  llm_generated?: boolean;
}

function RecommendationContent({ data }: { data: Record<string, unknown> | null }) {
  if (!data || Object.keys(data).length === 0) return null;

  const action         = data['action']                as string | undefined;
  const recommendation = data['recommendation']        as string | undefined;
  const summary        = data['summary']               as string | undefined;
  const currentValue   = data['current_value']         as string | undefined;
  const suggestedValue = data['suggested_value']       as string | undefined;
  const currentTitle   = data['current_title']         as string | undefined;
  const suggestedTitle = data['suggested_title']       as string | undefined;
  const currentDesc    = data['current_description']   as string | undefined;
  const suggestedDesc  = data['suggested_description'] as string | undefined;
  const steps          = data['steps']                 as string[] | undefined;
  const reasoning      = data['reasoning']             as string | undefined;

  const extraEntries = Object.entries(data).filter(([k]) => !KNOWN_KEYS.has(k));
  const primaryText = action || recommendation || summary;

  return (
    <Box>
      {primaryText && (
        <Box sx={{ bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200', borderRadius: 1, px: 2, py: 1.5, mb: 1.5 }}>
          <Typography variant="body1" fontWeight={500} sx={{ lineHeight: 1.75 }}>
            {primaryText}
          </Typography>
        </Box>
      )}
      {(currentTitle || suggestedTitle) && (
        <BeforeAfter label="Title Tag" before={currentTitle} after={suggestedTitle} />
      )}
      {(currentDesc || suggestedDesc) && (
        <BeforeAfter label="Meta Description" before={currentDesc} after={suggestedDesc} />
      )}
      {(currentValue || suggestedValue) && !currentTitle && !currentDesc && (
        <BeforeAfter label="Suggested Change" before={currentValue} after={suggestedValue} />
      )}
      {steps && steps.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ mb: 0.75, letterSpacing: '0.05em' }}>
            STEPS TO IMPLEMENT
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
            {steps.map((step, i) => (
              <Typography key={i} component="li" variant="body2" sx={{ mb: 0.5, lineHeight: 1.65 }}>
                {step}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
      {extraEntries.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
          {extraEntries.map(([key, val]) => (
            <Box key={key}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'capitalize', mb: 0.25 }}>
                {key.replace(/_/g, ' ')}
              </Typography>
              <Typography variant="body2">
                {typeof val === 'object' ? JSON.stringify(val) : String(val)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      {/* ── AI Reasoning accordion (on card) ── */}
      {reasoning && (
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            mt: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '6px !important',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon fontSize="small" />}
            sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.75 } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReasoningIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
              <Typography variant="caption" fontWeight={700} sx={{ letterSpacing: '0.06em', color: 'text.secondary' }}>
                AI REASONING
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 2 }}>
            <Typography variant="body2" sx={{ lineHeight: 1.75, color: 'text.secondary' }}>
              {reasoning}
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
}

// ─── Single recommendation card ──────────────────────────────────────────────

function RecommendationCard({
  rec,
  runId,
}: {
  rec: AuditRecommendation;
  runId: string;
}) {
  const queryClient = useQueryClient();
  const [detailOpen, setDetailOpen] = useState(false);
  const [ignoreOpen, setIgnoreOpen] = useState(false);
  const [ignoreReason, setIgnoreReason] = useState('');
  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' }>({
    open: false, msg: '', severity: 'success',
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['audit-recommendations', runId] });

  const applyMutation = useMutation({
    mutationFn: () => applyRecommendation(rec.id),
    onSuccess: () => {
      setSnack({ open: true, msg: "Marked as applied! We'll track the impact over the coming days.", severity: 'success' });
      invalidate();
    },
    onError: (e: Error) => setSnack({ open: true, msg: e.message, severity: 'error' }),
  });

  const ignoreMutation = useMutation({
    mutationFn: () => rejectRecommendation(rec.id, ignoreReason),
    onSuccess: () => {
      setSnack({ open: true, msg: 'Recommendation dismissed.', severity: 'success' });
      setIgnoreOpen(false);
      setIgnoreReason('');
      invalidate();
    },
    onError: (e: Error) => setSnack({ open: true, msg: e.message, severity: 'error' }),
  });

  const isPending    = applyMutation.isPending || ignoreMutation.isPending;
  const isApplied    = rec.status === 'applied' || rec.status === 'measuring' || rec.status === 'completed';
  const isIgnored    = rec.status === 'rejected';
  const isActionable = rec.status === 'approved';

  // Items-based recommendation data (one DB record = many bundled items)
  const items = (rec.recommendationData?.['items'] as RecommendationItem[] | undefined) ?? null;
  const hasItems = items !== null && items.length > 0;
  const [itemIdx, setItemIdx] = useState(0);
  const currentItem = hasItems ? items[Math.min(itemIdx, items.length - 1)] : null;

  // Extract LLM internals from currentItem (items[] mode) or top-level recommendationData (legacy)
  const activeData: Record<string, unknown> = currentItem
    ? (currentItem as unknown as Record<string, unknown>)
    : (rec.recommendationData ?? {});
  const dlgReasoning = activeData['reasoning']    != null ? String(activeData['reasoning'])    : undefined;
  const dlgLlmPrompt = activeData['llm_prompt']   != null ? String(activeData['llm_prompt'])   : undefined;
  const dlgLlmResp   = activeData['llm_response'] != null ? String(activeData['llm_response']) : undefined;
  const dlgLlmModel  = activeData['llm_model']    != null ? String(activeData['llm_model'])    : undefined;

  // Only surface recs the admin has vetted (approved) or the customer has already acted on
  if (!isActionable && !isApplied && !isIgnored) return null;

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          mb: 2,
          opacity: isIgnored ? 0.6 : 1,
          borderColor: isApplied ? 'success.main' : isActionable ? 'primary.light' : 'divider',
          borderWidth: isActionable ? 1.5 : 1,
        }}
      >
        {/* ── Row 1: category/priority chips (left) + item stepper (right top) ── */}
        <CardContent sx={{ pb: 0.5, pt: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Chip
              label={hasItems
                ? (currentItem!.category ?? rec.recommendationType?.replace(/_/g, ' '))
                : rec.recommendationType?.replace(/_/g, ' ')}
              size="small"
              variant="outlined"
            />
            {(() => {
              const p = (hasItems ? currentItem!.priority : rec.priority)?.toLowerCase();
              return p ? <Chip label={`${p} priority`} size="small" color={PRIORITY_COLOR[p] ?? 'default'} /> : null;
            })()}
            {isApplied && <Chip icon={<AppliedIcon fontSize="small" />} label="Applied" size="small" color="success" />}
            {isIgnored && <Chip label="Ignored" size="small" variant="outlined" />}
            {/* Item stepper — visible when multiple items bundled in one record (right top) */}
            {hasItems && items!.length > 1 && (
              <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', ml: 'auto' }}>
                <IconButton
                  size="small"
                  disabled={itemIdx === 0}
                  onClick={(e) => { e.stopPropagation(); setItemIdx(i => Math.max(0, i - 1)); }}
                  sx={{ p: 0.25, borderRadius: 0 }}
                >
                  <ChevronLeftIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <Typography variant="caption" fontWeight={700} sx={{ px: 1, userSelect: 'none' }}>
                  {itemIdx + 1} / {items!.length}
                </Typography>
                <IconButton
                  size="small"
                  disabled={itemIdx === items!.length - 1}
                  onClick={(e) => { e.stopPropagation(); setItemIdx(i => Math.min(items!.length - 1, i + 1)); }}
                  sx={{ p: 0.25, borderRadius: 0 }}
                >
                  <ChevronRightIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            )}
          </Box>
          {/* Page URL — left bottom */}
          {rec.targetUrl && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
              <Typography
                variant="caption"
                sx={{ fontFamily: 'monospace', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {rec.targetUrl}
              </Typography>
              <IconButton size="small" href={rec.targetUrl} target="_blank" sx={{ p: 0.25, flexShrink: 0 }}>
                <LinkIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Box>
          )}
        </CardContent>

        <Divider />

        {/* ── Row 2: PRIMARY – title + what to do + why this matters ── */}
        <CardContent sx={{ py: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, lineHeight: 1.3 }}>
            {hasItems ? currentItem!.title : rec.title}
          </Typography>
          {hasItems ? (
            <>
              {currentItem!.issue && (
                <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 2, py: 1.25, mb: 1.5 }}>
                  <Typography variant="body2" sx={{ lineHeight: 1.7 }}>{currentItem!.issue}</Typography>
                </Box>
              )}
              {(currentItem!.current_value || currentItem!.suggested_value) && (
                <BeforeAfter
                  label="Suggested Change"
                  before={currentItem!.current_value ?? undefined}
                  after={currentItem!.suggested_value ?? undefined}
                />
              )}
              {currentItem!.reasoning && (
                <Accordion disableGutters elevation={0} sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: '6px !important', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.75 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ReasoningIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                      <Typography variant="caption" fontWeight={700} sx={{ letterSpacing: '0.06em', color: 'text.secondary' }}>AI REASONING</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 2 }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.75, color: 'text.secondary' }}>{currentItem!.reasoning}</Typography>
                  </AccordionDetails>
                </Accordion>
              )}
            </>
          ) : rec.recommendationData && Object.keys(rec.recommendationData).length > 0 ? (
            <>
              <RecommendationContent data={rec.recommendationData} />
              {rec.issue && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="overline" color="text.secondary" display="block" sx={{ lineHeight: 1.6, mb: 0.5 }}>
                    Why this matters
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {rec.issue}
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 2, py: 1.5 }}>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                {rec.issue}
              </Typography>
            </Box>
          )}
        </CardContent>

        {/* ── Row 3: SECONDARY – compact horizontal metadata ── */}
        {(() => {
          const activeImpact = hasItems ? currentItem!.impact  : rec.impact;
          const activeEffort = hasItems ? currentItem!.effort  : rec.effort;
          const activeQuery  = hasItems ? (currentItem!.target_query ?? null) : rec.targetQuery;
          const showRow3 = activeImpact || activeEffort || activeQuery
            || (!hasItems && (rec.riskLevel || rec.confidence != null));
          if (!showRow3) return null;
          return (
            <>
              <Divider />
              <CardContent sx={{ py: 1.25 }}>
                <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  {activeImpact && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="caption" color="text.secondary">Impact</Typography>
                      <Chip label={activeImpact} size="small" color={activeImpact === 'high' ? 'success' : activeImpact === 'medium' ? 'warning' : 'default'} />
                    </Box>
                  )}
                  {activeEffort && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="caption" color="text.secondary">Effort</Typography>
                      <Chip label={activeEffort} size="small" />
                    </Box>
                  )}
                  {!hasItems && rec.riskLevel && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="caption" color="text.secondary">Risk</Typography>
                      <Chip label={rec.riskLevel} size="small" color={rec.riskLevel === 'high' ? 'error' : rec.riskLevel === 'medium' ? 'warning' : 'default'} />
                    </Box>
                  )}
                  {!hasItems && rec.confidence != null && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">Confidence</Typography>
                      <LinearProgress
                        variant="determinate"
                        value={rec.confidence * 100}
                        sx={{ width: 64, height: 6, borderRadius: 3 }}
                        color={rec.confidence > 0.7 ? 'success' : rec.confidence > 0.4 ? 'warning' : 'error'}
                      />
                      <Typography variant="caption" fontWeight={600}>{(rec.confidence * 100).toFixed(0)}%</Typography>
                    </Box>
                  )}
                  {activeQuery && (
                    <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                      "{activeQuery}"
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </>
          );
        })()}

        {/* ── Row 4: action bar ── */}
        <Divider />
        <CardActions sx={{ px: 2, py: 1.25, gap: 1, justifyContent: 'space-between', flexWrap: 'wrap' }}>
          {isActionable ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={isPending ? <CircularProgress size={14} /> : <ActionIcon />}
                disabled={isPending}
                onClick={() => applyMutation.mutate()}
              >
                Take Action
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<IgnoreIcon fontSize="small" />}
                disabled={isPending}
                onClick={() => setIgnoreOpen(true)}
                sx={{ color: 'text.secondary' }}
              >
                Ignore
              </Button>
            </Box>
          ) : (
            <Box />
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Generated {fmt(rec.createdAt)}
            </Typography>
            <Tooltip title="Full details">
              <IconButton size="small" onClick={() => setDetailOpen(true)}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </CardActions>
      </Card>

      {/* Detail dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <InfoIcon color="primary" />
            <Typography variant="h6" component="span">
              {hasItems ? currentItem!.title : rec.title}
            </Typography>
            {hasItems && items!.length > 1 && (
              <Chip label={`${itemIdx + 1} of ${items!.length}`} size="small" variant="outlined" sx={{ ml: 0.5 }} />
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '72vh' }}>
          <Grid container spacing={2}>
            {/* Issue summary */}
            <Grid item xs={12}>
              <Alert severity="info" icon={false}>
                <Typography variant="body2">{hasItems ? currentItem!.issue : rec.issue}</Typography>
              </Alert>
            </Grid>

            {/* All items navigator — shown when multiple items bundled */}
            {hasItems && items!.length > 1 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  All {items!.length} Recommendations
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {items!.map((itm, i) => (
                    <Box
                      key={i}
                      onClick={() => setItemIdx(i)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        p: 1, borderRadius: 1, cursor: 'pointer',
                        bgcolor: i === itemIdx ? 'primary.50' : 'transparent',
                        border: '1px solid', borderColor: i === itemIdx ? 'primary.main' : 'divider',
                        '&:hover': { bgcolor: i === itemIdx ? 'primary.50' : 'action.hover' },
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 700, color: i === itemIdx ? 'primary.main' : 'text.secondary', minWidth: 20 }}>
                        {i + 1}.
                      </Typography>
                      {itm.category && <Chip label={itm.category} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />}
                      <Typography variant="body2" sx={{ flex: 1, fontWeight: i === itemIdx ? 600 : 400, lineHeight: 1.4 }}>
                        {itm.title}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
            )}

            {/* Target */}
            {(rec.targetUrl || rec.targetQuery) && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ borderBottom: 1, borderColor: 'divider', pb: 0.5 }}>
                    Target
                  </Typography>
                </Grid>
                {rec.targetUrl && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">URL</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>
                        {rec.targetUrl}
                      </Typography>
                      <IconButton size="small" href={rec.targetUrl} target="_blank">
                        <LinkIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  </Grid>
                )}
                {rec.targetQuery && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Target Search Query</Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.25 }}>"{rec.targetQuery}"</Typography>
                  </Grid>
                )}
              </>
            )}

            {/* Classification */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ borderBottom: 1, borderColor: 'divider', pb: 0.5 }}>
                Classification
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Type</Typography>
              <Box mt={0.25}><Chip label={rec.recommendationType?.replace(/_/g, ' ')} size="small" variant="outlined" /></Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Priority</Typography>
              <Box mt={0.25}>
                {rec.priority
                  ? <Chip label={rec.priority} size="small" color={PRIORITY_COLOR[rec.priority] ?? 'default'} />
                  : <Typography variant="body2">—</Typography>}
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Box mt={0.25}>
                <Chip label={rec.status.replace(/_/g, ' ')} size="small" variant="outlined" />
              </Box>
            </Grid>
            {rec.agent && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Agent</Typography>
                <Box mt={0.25}><Chip label={rec.agent} size="small" /></Box>
              </Grid>
            )}

            {/* Assessment */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ borderBottom: 1, borderColor: 'divider', pb: 0.5 }}>
                Assessment
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Expected Impact</Typography>
              <Box mt={0.25}>
                {(hasItems ? currentItem!.impact : rec.impact)
                  ? <Chip label={hasItems ? currentItem!.impact! : rec.impact!} size="small" color={(hasItems ? currentItem!.impact : rec.impact) === 'high' ? 'success' : 'default'} />
                  : <Typography variant="body2">—</Typography>}
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Effort Required</Typography>
              <Box mt={0.25}>
                {(hasItems ? currentItem!.effort : rec.effort)
                  ? <Chip label={hasItems ? currentItem!.effort! : rec.effort!} size="small" />
                  : <Typography variant="body2">—</Typography>}
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Risk Level</Typography>
              <Box mt={0.25}>
                {rec.riskLevel
                  ? <Chip label={rec.riskLevel} size="small" color={rec.riskLevel === 'high' ? 'error' : rec.riskLevel === 'medium' ? 'warning' : 'default'} />
                  : <Typography variant="body2">—</Typography>}
              </Box>
            </Grid>
            {rec.confidence != null && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">AI Confidence</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                  <LinearProgress
                    variant="determinate"
                    value={rec.confidence * 100}
                    sx={{ flex: 1, height: 8, borderRadius: 4 }}
                    color={rec.confidence > 0.7 ? 'success' : rec.confidence > 0.4 ? 'warning' : 'error'}
                  />
                  <Typography variant="body2" fontWeight={600}>{(rec.confidence * 100).toFixed(0)}%</Typography>
                </Box>
              </Grid>
            )}

            {/* ── LLM Internals ── */}
            {(dlgReasoning || dlgLlmPrompt || dlgLlmResp) && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ borderBottom: 1, borderColor: 'divider', pb: 0.5, mb: 1 }}>
                  AI Details
                </Typography>

                {/* Reasoning */}
                {dlgReasoning && (
                  <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '6px !important', mb: 1, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.75 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ReasoningIcon fontSize="small" color="action" />
                        <Typography variant="caption" fontWeight={700} sx={{ letterSpacing: '0.06em' }}>AI REASONING</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
                      <Typography variant="body2" sx={{ lineHeight: 1.8, color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
                        {dlgReasoning}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* LLM Prompt */}
                {dlgLlmPrompt && (() => {
                  let parsed: { system?: string; user?: string } | null = null;
                  try { parsed = JSON.parse(dlgLlmPrompt); } catch { /* raw string fallback */ }
                  return (
                    <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '6px !important', mb: 1, '&:before': { display: 'none' } }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.75 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PromptIcon fontSize="small" color="action" />
                          <Typography variant="caption" fontWeight={700} sx={{ letterSpacing: '0.06em' }}>LLM PROMPT</Typography>
                          {dlgLlmModel && <Chip label={dlgLlmModel} size="small" variant="outlined" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 0 }}>
                        {parsed ? (
                          <Box>
                            {parsed.system && (
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="caption" fontWeight={700} sx={{ px: 2, display: 'block', color: 'text.secondary', letterSpacing: '0.06em', mb: 0.5 }}>SYSTEM</Typography>
                                <Box sx={{ bgcolor: 'action.hover', px: 2, py: 1.5, maxHeight: 220, overflowY: 'auto' }}>
                                  <Typography variant="body2" component="pre" sx={{ m: 0, fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {parsed.system}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                            {parsed.user && (
                              <Box>
                                <Typography variant="caption" fontWeight={700} sx={{ px: 2, display: 'block', color: 'text.secondary', letterSpacing: '0.06em', mb: 0.5 }}>USER</Typography>
                                <Box sx={{ bgcolor: 'action.hover', px: 2, py: 1.5, maxHeight: 220, overflowY: 'auto' }}>
                                  <Typography variant="body2" component="pre" sx={{ m: 0, fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {parsed.user}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        ) : (
                          <Box sx={{ bgcolor: 'action.hover', px: 2, py: 1.5, maxHeight: 280, overflowY: 'auto' }}>
                            <Typography variant="body2" component="pre" sx={{ m: 0, fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {dlgLlmPrompt}
                            </Typography>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })()}

                {/* LLM Response */}
                {dlgLlmResp && (() => {
                  let pretty = dlgLlmResp;
                  try { pretty = JSON.stringify(JSON.parse(dlgLlmResp), null, 2); } catch { /* keep raw */ }
                  return (
                    <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '6px !important', mb: 1, '&:before': { display: 'none' } }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.75 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ResponseIcon fontSize="small" color="action" />
                          <Typography variant="caption" fontWeight={700} sx={{ letterSpacing: '0.06em' }}>LLM RESPONSE</Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 0 }}>
                        <Box sx={{ bgcolor: 'action.hover', px: 2, py: 1.5, maxHeight: 320, overflowY: 'auto' }}>
                          <Typography variant="body2" component="pre" sx={{ m: 0, fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {pretty}
                          </Typography>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  );
                })()}
              </Grid>
            )}

            {/* Timestamps */}
            <Grid item xs={6} sm={4}>
              <Typography variant="caption" color="text.secondary">Generated</Typography>
              <Typography variant="body2" mt={0.25}>{fmt(rec.createdAt)}</Typography>
            </Grid>
            {rec.updatedAt && rec.updatedAt !== rec.createdAt && (
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">Last Updated</Typography>
                <Typography variant="body2" mt={0.25}>{fmt(rec.updatedAt)}</Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
          {isActionable && (
            <>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={applyMutation.isPending ? <CircularProgress size={14} /> : <ActionIcon />}
                disabled={isPending}
                onClick={() => { applyMutation.mutate(); setDetailOpen(false); }}
              >
                Take Action
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={<IgnoreIcon fontSize="small" />}
                disabled={isPending}
                onClick={() => { setDetailOpen(false); setIgnoreOpen(true); }}
                sx={{ color: 'text.secondary' }}
              >
                Ignore
              </Button>
            </>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Ignore dialog */}
      <Dialog open={ignoreOpen} onClose={() => setIgnoreOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ignore Recommendation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Not relevant to your site? Let us know why — this helps improve future suggestions.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Reason (optional)"
            value={ignoreReason}
            onChange={e => setIgnoreReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIgnoreOpen(false)}>Cancel</Button>
          <Button
            variant="outlined"
            color="inherit"
            disabled={ignoreMutation.isPending}
            onClick={() => ignoreMutation.mutate()}
          >
            {ignoreMutation.isPending ? <CircularProgress size={18} /> : 'Ignore'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}

// ─── Detail view: recommendations for a specific runId ────────────────────────

function RecommendationsDetail({
  runId,
  requestId,
}: {
  runId: string;
  requestId?: string;
}) {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['audit-recommendations', runId],
    queryFn: () => getAuditRecommendations(runId),
  });

  const recs: AuditRecommendation[] = data?.data ?? [];
  const actionable    = recs.filter(r => r.status === 'approved');
  const done          = recs.filter(r => r.status === 'applied' || r.status === 'measuring' || r.status === 'completed');
  const ignored       = recs.filter(r => r.status === 'rejected');
  const awaitingAdmin = recs.filter(
    r => r.status === 'pending_review' || r.status === 'pending_approval' || r.status === 'pending_llm_response'
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Button
          component={Link}
          to="/customer/audit/tracking?tab=1"
          size="small"
          variant="outlined"
          startIcon={<BackIcon fontSize="small" />}
        >
          Audit History
        </Button>
        <Typography variant="subtitle1" fontWeight={600}>
          {requestId ? `Recommendations for Request #${requestId}` : 'Recommendations'}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? <CircularProgress size={16} /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{(error as Error).message}</Alert>}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary pills */}
          {recs.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              {actionable.length > 0    && <Chip label={`${actionable.length} ready for action`}    size="small" color="primary" />}
              {done.length > 0          && <Chip icon={<AppliedIcon fontSize="small" />} label={`${done.length} applied`} size="small" color="success" />}
              {ignored.length > 0       && <Chip label={`${ignored.length} ignored`}       size="small" variant="outlined" />}
              {awaitingAdmin.length > 0 && <Chip label={`${awaitingAdmin.length} pending review`} size="small" color="warning" />}
            </Box>
          )}

          {/* Awaiting admin quality review */}
          {awaitingAdmin.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Recommendations are being reviewed</AlertTitle>
              {awaitingAdmin.length} recommendation{awaitingAdmin.length !== 1 ? 's are' : ' is'} currently
              being checked for quality. They will appear here once ready.
            </Alert>
          )}

          {/* Actionable (admin-approved) */}
          {actionable.length > 0 && (
            <>
              <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Ready for action — {actionable.length}
              </Typography>
              {actionable.map(rec => (
                <RecommendationCard key={rec.id} rec={rec} runId={runId} />
              ))}
            </>
          )}

          {/* Applied */}
          {done.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Applied — {done.length}
              </Typography>
              {done.map(rec => (
                <RecommendationCard key={rec.id} rec={rec} runId={runId} />
              ))}
            </>
          )}

          {/* Ignored */}
          {ignored.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Ignored — {ignored.length}
              </Typography>
              {ignored.map(rec => (
                <RecommendationCard key={rec.id} rec={rec} runId={runId} />
              ))}
            </>
          )}

          {/* Empty state */}
          {actionable.length === 0 && done.length === 0 && ignored.length === 0 && awaitingAdmin.length === 0 && (
            <Alert severity="info">
              No recommendations found for this audit run. The AI analysis may still be processing.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
}

// ─── List view: completed page audits with recommendations ────────────────────

function RecommendationsList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['page-audit-completed-recs', page],
    queryFn: () => listPageAuditRequests({ status: 'COMPLETED', page, size: PAGE_SIZE }),
    staleTime: 30_000,
  });

  const rows: PageAuditRequest[] = (data?.data ?? []).filter(
    (r) => (r.recommendation_count ?? 0) > 0
  );
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? <CircularProgress size={18} /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{(error as Error).message}</Alert>}

      <Card variant="outlined">
        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.default' }}>
                <TableCell sx={{ fontWeight: 600 }}>Page</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Recommendations</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>AI Analysis</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Completed</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No completed audits with recommendations yet.{' '}
                    <Link to="/customer/audit/page">Submit a page audit</Link> to get started.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ maxWidth: 260 }}>
                      {row.page_url ? (
                        <Tooltip title={row.page_url}>
                          <Typography
                            variant="caption"
                            sx={{ fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: 240 }}
                          >
                            {row.page_url}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Chip label="Full-site" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<RecsIcon fontSize="small" />}
                        label={`${row.recommendation_count} recommendations`}
                        size="small"
                        color="success"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.llm_enabled ? 'AI On' : 'Off'}
                        size="small"
                        variant="outlined"
                        color={row.llm_enabled ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                      {fmt(row.completed_at)}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!row.run_id}
                        onClick={() =>
                          navigate(
                            `/customer/audit/recommendations?runId=${encodeURIComponent(row.run_id ?? '')}&requestId=${row.id}`
                          )
                        }
                      >
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {total > PAGE_SIZE && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5 }}>
            <Typography variant="caption" color="text.secondary">{total} completed audits</Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, p) => setPage(p)}
              size="small"
              color="primary"
            />
          </Box>
        )}
      </Card>
    </Box>
  );
}

// ─── main page ────────────────────────────────────────────────────────────────

export default function CustomerAuditRecommendationsPage() {
  const [searchParams] = useSearchParams();
  const runId = searchParams.get('runId');
  const requestId = searchParams.get('requestId') ?? undefined;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>SEO Recommendations</Typography>
        <Typography variant="body2" color="text.secondary">
          {runId
            ? 'Approved SEO recommendations for this audit — take action on ones you want to implement, or ignore the rest.'
            : 'Your completed audits with approved SEO recommendations waiting for your action.'}
        </Typography>
      </Box>

      {runId ? (
        <RecommendationsDetail runId={runId} requestId={requestId} />
      ) : (
        <RecommendationsList />
      )}
    </Box>
  );
}
